import {
  Box,
  Button,
  InlineStack,
  Banner,
  BlockStack,
} from "@medentic/polaris-react";
import { useQuery } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ROOTS } from "@app/config/constants";
import { TopMessage } from "./types";
import { dismissMessage, setMessages } from "./topMessagesSlice";
import { RootState } from "@app/store/store";
import { useEffect } from "react";
import TranslateLink from "../TranslateLink";
import { PageTypes } from "@shared/types/commonTypes";
import usePageType from "@shared/hooks/navigation/usePageType";

export default function TopMessages() {
  const { GET } = useApiRequest();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pageType = usePageType();

  const { clinicId, eventOrganizerId } = useParams();
  const hccId = pageType === PageTypes.CLINIC ? clinicId : eventOrganizerId;
  const topMessagesUrl =
    pageType === PageTypes.CLINIC
      ? `/api/healthcare-companies/${hccId}/top-messages`
      : `/api/healthcare-companies/${hccId}/e/top-messages`;

  const { messages, hasLoaded } = useSelector(
    (state: RootState) => state.topMessages
  );

  const {
    data: fetchedMessages = [],
    isSuccess,
  } = useQuery<TopMessage[]>({
    queryKey: ["topMessages"],
    queryFn: () => GET(topMessagesUrl),
  });

  useEffect(() => {
    if (isSuccess && fetchedMessages.length && !hasLoaded) {
      dispatch(setMessages(fetchedMessages));
    }
  }, [isSuccess, fetchedMessages, dispatch, hasLoaded]);

  const handleDismiss = (id: string) => {
    dispatch(dismissMessage(id));
  };

  return (
    <>
      {messages.map((message) => (
        <Box paddingBlockEnd="200" key={message.id}>
          <Banner
            title={message.title}
            tone={message.tone}
            onDismiss={
              !message.hideCloseIcon
                ? () => handleDismiss(message.id)
                : undefined
            }
          >
            <BlockStack gap="400">
              <TranslateLink text={message.description} />

              <InlineStack gap="400">
                {message.action && (
                  <Button
                    variant={
                      message.action.type === "external_url"
                        ? "primary"
                        : "secondary"
                    }
                    onClick={() =>
                      message.action.type === "external_url"
                        ? window.open(message.action.url, "_blank")
                        : navigate(
                            `${ROOTS.CLINIC}/${clinicId}${message.action.url}`
                          )
                    }
                  >
                    {message.action.label}
                  </Button>
                )}
                {message.secondaryAction && (
                  <Button
                    onClick={() =>
                      message.secondaryAction?.type === "internal_url"
                        ? navigate(
                            `${ROOTS.CLINIC}/${clinicId}${message.secondaryAction.url}`
                          )
                        : window.open(message.secondaryAction?.url, "_blank")
                    }
                  >
                    {message.secondaryAction.label}
                  </Button>
                )}
              </InlineStack>
            </BlockStack>
          </Banner>
        </Box>
      ))}
    </>
  );
}
